import React from "react";
import { useTranslation } from "react-i18next";
import type {
  MigrationDeploymentMatching,
  MigrationIdentificationCounts,
  MigrationMatching,
  MigrationMatchReason,
  MigrationMatchStep,
  MigrationNotice,
  MigrationPlan,
  MigrationProjectSection,
  MigrationTotals,
} from "../../api/migrationsApi";
import tailwindClassnames from "../common/TailwindClassnames";

/**
 * Renders a Wildlife Insights migration report (AKL-747).
 *
 * Used for both the dry run — what applying would do — and the result of an apply. They
 * are the same shape because the server walks the bundle through one code path in both
 * modes, so one renderer is correct rather than merely convenient.
 *
 * The server sends notice **codes and parameters**, never sentences: the report is stored
 * once on the job row and read later, possibly by someone using a different language. All
 * the wording therefore lives here and in the four translation catalogs.
 */

/** Akili field keys the server reports as missing, mapped to names a researcher uses. */
const PROJECT_FIELD_LABELS: Record<string, string> = {
  project_name: "Project name",
  short_name: "Short name",
  country_id: "Country",
  objectives: "Objectives",
  species_focus: "Species focus",
  sensor_layout: "Sensor layout",
  capture_method: "Capture method",
  bait_used: "Bait use",
  blank_images_removed: "Blank images removed",
  photos_license: "Image licence",
  metadata_license: "Metadata licence",
  primary_contact_name: "Project contact name",
  primary_contact_email: "Project contact email",
  timezone: "Time zone",
};

type TFunction = ReturnType<typeof useTranslation>["t"];

/** One entry of the `project_fields_too_long` notice's `fields` parameter (AKL-728). */
type OverLongField = { field: string; limit: number; actual: number };

/**
 * Is this one well-formed entry of that notice's `fields` array?
 *
 * `MigrationNotice.params` is `Record<string, unknown>` read back from a stored job row,
 * so its shape is a promise rather than a guarantee — a server ahead of this front end, or
 * a malformed row, can put anything there. Checked rather than cast, because this module's
 * whole contract is that it always returns a readable sentence and never throws.
 * @param value one element of the raw `fields` parameter
 * @returns true when it carries the field name and both numbers the sentence needs
 */
const isOverLongField = (value: unknown): value is OverLongField => {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Partial<OverLongField>;
  return (
    typeof entry.field === "string" &&
    typeof entry.limit === "number" &&
    typeof entry.actual === "number"
  );
};

/**
 * Why a bundle's Wildlife Insights AI guesses could not be stored (AKL-754). Shared by the
 * `prediction_model_unavailable` skipped row and the `wi_prediction_model_unavailable`
 * warning, so the two places can never say different things about the same missing row.
 */
const PREDICTION_MODEL_UNAVAILABLE_TEXT =
  "akili is missing the Wildlife Insights model record that holds AI guesses, so the guesses in this bundle were not brought across. Everything else was migrated as usual.";

/**
 * One notice, as a sentence in the reader's language.
 *
 * An unrecognised code renders as a readable fallback rather than blank or a crash: a
 * server ahead of a deployed front end should still tell the user *something*, and a
 * silently dropped blocking notice would leave them staring at a disabled button with no
 * explanation.
 * @param notice the code and parameters from the report
 * @param t the translation function
 * @returns the sentence to display
 */
export const migrationNoticeText = (
  notice: MigrationNotice,
  t: TFunction,
): string => {
  const params = notice.params ?? {};
  switch (notice.code) {
    case "vocabulary_unmapped":
      return t(
        "{{field}}: akili has no equivalent for the Wildlife Insights value “{{value}}”, so this setting was left blank.",
        params,
      );
    case "country_unrecognised":
      return t(
        "“{{value}}” is not a country code akili recognises, so the country was left blank.",
        params,
      );
    case "licence_narrowed":
      return t(
        "akili has no “{{value}}” licence option, so {{field}} was recorded as the more restrictive CC BY-NC. Check this before publishing.",
        params,
      );
    case "licence_unrecognised":
      return t(
        "{{field}}: “{{value}}” is not a licence akili recognises, so it was left blank.",
        params,
      );
    case "schema_value_unknown":
      return t(
        "{{field}}: “{{value}}” is not one of akili's options. It has been kept as-is, so the deployment form may show it as an unrecognised value.",
        params,
      );
    case "unrecognised_columns":
      return t(
        "This bundle has columns akili does not read yet, so they were not migrated: {{tables}}",
        params,
      );
    case "purchase_year_only":
      return t(
        "Camera {{camera}}: Wildlife Insights records only the year of purchase ({{year}}), so it was stored as 1 January {{year}}.",
        params,
      );
    case "month_first_assumed":
      return t(
        "Some deployment dates were not in year-month-day order, so they were read month-first, the Wildlife Insights convention. Check the deployment dates afterwards.",
      );
    case "new_project_no_photos_yet":
      return t(
        "This will create a new project, so there are no images in akili yet for the identifications to attach to. Apply this migration, upload your images with Akili Transfer, then run the same bundle again.",
      );
    case "target_project_missing":
      return t("Project {{project_id}} no longer exists.", params);
    case "no_projects_csv":
      return t(
        "This bundle has no projects.csv, so there is nothing to create a project from. Choose an existing project to migrate into.",
      );
    case "no_project_match":
      return t(
        "No akili project matches this bundle. Choose an existing project to migrate into, or choose to create a new one.",
      );
    case "project_missing_fields":
      return t(
        "Wildlife Insights did not supply everything akili needs to create a project: {{fields}}",
        {
          fields: ((params.fields as string[]) ?? [])
            .map((field) => t(PROJECT_FIELD_LABELS[field] ?? field))
            .join(", "),
        },
      );
    case "project_fields_too_long": {
      // AKL-728. The approved sentence is composed from two strings rather than one, so
      // that several over-long fields read as a list of clauses followed by a single
      // instruction instead of repeating "Shorten it..." per field. For the ordinary
      // one-field case the two join into exactly the approved wording.
      //
      // `params.fields` is validated rather than cast: it comes back from a stored job
      // row, so a missing key, a null, or a non-array would previously have thrown inside
      // `.map()` and taken the whole report down with it — the opposite of this function's
      // fallback-always contract. Anything unusable falls through to the code-name
      // fallback below rather than inventing a sentence.
      const raw = params.fields;
      const fields = Array.isArray(raw) ? raw.filter(isOverLongField) : [];
      if (fields.length > 0) {
        const clauses = fields.map((entry) =>
          t(
            "Wildlife Insights supplied a {{field}} of {{actual}} characters, but akili allows {{limit}}.",
            {
              field: t(PROJECT_FIELD_LABELS[entry.field] ?? entry.field),
              actual: entry.actual,
              limit: entry.limit,
            },
          ),
        );
        return [
          ...clauses,
          t("Shorten it in Wildlife Insights and download the bundle again."),
        ].join(" ");
      }
      break;
    }
    case "project_permission_denied":
      return t(
        "This bundle matches the project “{{project_name}}”, but you do not have permission to migrate data into it. Ask one of its owners for access, or choose a different project.",
        params,
      );
    case "target_with_many_projects":
      return t(
        "This bundle describes {{count}} Wildlife Insights projects, so it cannot be migrated into a single akili project. Migrate it from the organisation instead.",
        params,
      );
    case "rows_for_unknown_project":
      return t(
        "{{count}} identifications name a Wildlife Insights project that is not described in this bundle, so there was nothing to attach them to.",
        params,
      );
    case "sequence_fan_out_unavailable":
      return t(
        "This is a Wildlife Insights sequence project, but its images.csv has no sequence_id column, so identifications cannot be matched to individual images. Download the bundle from Wildlife Insights again.",
      );
    case "project_deployment_template_differs":
      // AKL-817. Bundle-level: the fields the bundle writes into every deployment's
      // schema_data would neither show in this project's form nor match its own
      // deployments, so nothing is written until the user changes one or the other.
      return t(
        "The project “{{project_name}}” uses a different deployment template than Wildlife Insights data needs. Change its deployment template to {{template}} in the project settings, or migrate into a different project.",
        params,
      );
    case "deployment_template_unavailable":
      return t(
        "The {{template}} deployment template could not be found, so it was not applied to “{{project_name}}”. Choose a deployment template in the project settings so the migrated deployment fields are shown.",
        params,
      );
    case "nothing_to_migrate":
      // AKL-818. Bundle-level, raised when every project in the bundle was skipped for
      // its own reason; the per-project sections below carry those reasons.
      return t(
        "Nothing in this bundle can be migrated: every project in it is blocked. Each project's section below says why.",
      );
    case "wi_prediction_model_unavailable":
      // AKL-754. Bundle-level warning, not a blocker: the guesses are counted under the
      // `prediction_model_unavailable` skipped row and everything else runs as before.
      return t(PREDICTION_MODEL_UNAVAILABLE_TEXT);
    default:
      return notice.code;
  }
  // Reached by the cases above that `break` out on malformed params (project_fields_too_long)
  // instead of returning: the fallback is the code, never an exception.
  return notice.code;
};

/**
 * The sentence for a project's deployment-template outcome, or null when there is nothing
 * to say inside the project's own section (AKL-817).
 *
 * Only `apply` and `already_applied` get a sentence here. `blocked` is already named in the
 * bundle-level alert and `unavailable` in the "Worth checking" list, and a report stored
 * before AKL-817 has no `deployment_template` key at all — all of those render nothing rather
 * than a placeholder, and so does an entry with no template name. Read defensively for the
 * same reason `migrationNoticeText` is: the value comes back from a stored job row.
 * @param template the section's `deployment_template`, if any
 * @param applied true when rendering the outcome of an apply rather than a preview
 * @param t the translation function
 * @returns the sentence to display, or null
 */
export const deploymentTemplateText = (
  template: MigrationProjectSection["deployment_template"],
  applied: boolean,
  t: TFunction,
): string | null => {
  if (!template || typeof template !== "object") return null;
  const name = typeof template.name === "string" ? template.name.trim() : "";
  // No name, no sentence: "The  deployment template will be applied" is worse than
  // silence, and the server always sends the name (PR #756 review).
  if (!name) return null;
  switch (template.outcome) {
    case "apply":
      return applied
        ? t(
            "The {{template}} deployment template was applied to this project.",
            { template: name },
          )
        : t(
            "The {{template}} deployment template will be applied to this project, so the migrated deployment fields are shown in the deployment form.",
            { template: name },
          );
    case "already_applied":
      return t(
        "This project already uses the {{template}} deployment template.",
        { template: name },
      );
    default:
      return null;
  }
};

/** The skip reasons worth explaining, in the order a user cares about them. */
const SKIP_EXPLANATIONS: {
  key: keyof MigrationIdentificationCounts["skipped"];
  label: string;
  detail: string;
}[] = [
  {
    key: "media_not_found",
    label: "No matching image in akili",
    detail:
      "These identifications refer to images that are not in akili yet. Upload them with Akili Transfer, then run this bundle again.",
  },
  {
    key: "media_awaiting_time",
    label: "Waiting for image times to be read",
    detail:
      "akili has not finished reading the time from these images, and the time is what tells apart images that share a file name. Run this bundle again in a few minutes.",
  },
  {
    // Since AKL-754 a run only counts a guess here when the migration's
    // `migrate_ai_predictions` option was off (round 2), or when the report predates the
    // feature. Either way the guess was simply not brought across, and the sentence says
    // how to bring it across now.
    key: "computer_vision",
    label: "Identified by Wildlife Insights AI, not reviewed",
    detail:
      "Wildlife Insights' AI predictions were not brought across for this migration: either the option “Bring Wildlife Insights' AI predictions across as suggestions in akili” was switched off, or the migration ran before akili offered it. Run the same bundle again with that option on to bring them across as suggestions for images nobody had identified.",
  },
  {
    // AKL-754. A person's identification is the finished answer for the image; the guess
    // it superseded stays in Wildlife Insights.
    key: "computer_vision_superseded",
    label: "Wildlife Insights AI guess on an identified image",
    detail:
      "A person had already identified these images in Wildlife Insights, so the AI's guess was not brought across.",
  },
  {
    // AKL-754. The same sentence as the bundle-level warning, on purpose: both describe
    // the same missing row, and one key cannot drift from the other.
    key: "prediction_model_unavailable",
    label: "Wildlife Insights AI guesses that could not be stored",
    detail: PREDICTION_MODEL_UNAVAILABLE_TEXT,
  },
  {
    key: "qa_artifact",
    label: "Camera setup and quality tags",
    detail:
      "Rows tagged Setup_Pickup, Timelapse, Misfire or No CV Result. These record something about the camera rather than something the camera saw.",
  },
  {
    key: "taxon_not_found",
    label: "Species not in akili's list",
    detail:
      "akili uses the Wildlife Insights species list, so this is unusual. These identifications were not brought across.",
  },
  {
    key: "media_ambiguous",
    label: "More than one image with the same file name",
    detail:
      "Two or more images in the same deployment share a file name and their times do not tell them apart, so there is no way to tell which one the identification belongs to.",
  },
  {
    key: "media_times_differ",
    label: "Image found, but the times do not match",
    detail:
      "An uploaded image has this file name, but its time and the time in Wildlife Insights disagree. If times were edited in Wildlife Insights, upload more of the deployment so akili can work out the difference, then run this bundle again.",
  },
  {
    key: "deployment_not_found",
    label: "Deployment not in akili",
    detail: "",
  },
  {
    key: "deployment_not_created_yet",
    label: "Deployment will be created by this migration",
    detail:
      "The deployment does not exist yet, so there are no images in it to match. They will match when you run this bundle again after uploading.",
  },
  {
    key: "no_taxon_id",
    label: "No species recorded",
    detail: "",
  },
  {
    key: "no_filename",
    label: "No file name recorded",
    detail: "",
  },
  {
    key: "sequence_fan_out_unavailable",
    label: "Sequence identifications could not be matched to images",
    detail: "",
  },
];

export const formatNumber = (value: number, language: string): string =>
  new Intl.NumberFormat(language).format(value);

/**
 * A clock difference as a signed hours:minutes:seconds figure, e.g. `+1:00:00`.
 *
 * Numeric on purpose: units would need a translation per language, and the figure is the
 * difference between two clocks, which reads the same in all of them. Positive means the
 * times in the export are later than the times in the uploaded images.
 */
export const formatClockShift = (seconds: number): string => {
  const sign = seconds < 0 ? "-" : "+";
  const total = Math.abs(Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  return `${sign}${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

/** The ways an image can be matched worth naming, in the order the server tries them. */
const MATCH_STEPS: { key: MigrationMatchStep; label: string }[] = [
  {
    key: "wi_file_id",
    label: "By the file name Wildlife Insights stored it under: {{total}}",
  },
  {
    key: "name_and_time",
    label: "By file name and the time the image was taken: {{total}}",
  },
  { key: "name_only", label: "By file name alone: {{total}}" },
];

/**
 * Why an image can stay unmatched, in the order the "Not migrated" list gives them.
 *
 * The wording is that list's own (`SKIP_EXPLANATIONS`), so a reason reads the same in both
 * places; only the unit differs (images here, identification rows there).
 */
const UNMATCHED_REASONS: MigrationMatchReason[] = [
  "media_not_found",
  "media_awaiting_time",
  "media_ambiguous",
  "media_times_differ",
];

/** The "Not migrated" list's label for a reason an image stayed unmatched. */
const unmatchedLabel = (reason: MigrationMatchReason): string =>
  SKIP_EXPLANATIONS.find(({ key }) => key === reason)?.label ?? reason;

/** How many deployments with a clock difference are listed before the rest are counted. */
const SHIFTED_DEPLOYMENTS_SHOWN = 10;

interface MatchingBlockProps {
  matching: MigrationMatching;
}

/**
 * How a project's Wildlife Insights images were matched to uploaded files (AKL-1125).
 *
 * Counts are images, not identification rows: the ways images were matched, and for each
 * reason an image stayed unmatched how many images it left. The "Not migrated" list below
 * counts the identification rows those images carried, which can be more. When a
 * deployment's times look edited in
 * Wildlife Insights the server works out the difference and matches through it; those
 * matches are the one place a wrong answer is possible, so they are called out and each
 * deployment's difference is shown for the reader to check before confirming.
 *
 * The server caps its deployment list (worst-first by unmatched images, not by clock
 * difference), so a deployment with shifted matches can be missing from it while the
 * block's own `shifted_photos` total still counts its images. The rest of the list is
 * therefore counted from that total: when the listed deployments do not account for all
 * of it, the remainder is stated in images, because how many deployments the server
 * dropped is not known.
 */
export const MatchingBlock: React.FC<MatchingBlockProps> = ({ matching }) => {
  const { t, i18n } = useTranslation();
  const number = (value: number) => formatNumber(value, i18n.language);
  const shifted = (matching.deployments ?? []).filter(
    (deployment) =>
      deployment.shift_seconds !== null && deployment.shifted_photos > 0,
  );
  const shown = shifted.slice(0, SHIFTED_DEPLOYMENTS_SHOWN);
  const imagesIn = (list: MigrationDeploymentMatching[]) =>
    list.reduce((sum, deployment) => sum + deployment.shifted_photos, 0);
  const hidden = Math.max(0, shifted.length - SHIFTED_DEPLOYMENTS_SHOWN);
  const serverDroppedSome = matching.shifted_photos > imagesIn(shifted);
  const imagesNotShown = Math.max(0, matching.shifted_photos - imagesIn(shown));
  const unmatchedReasons = UNMATCHED_REASONS.filter(
    (reason) => (matching.unmatched?.[reason] ?? 0) > 0,
  );

  return (
    <div>
      <h4 className={tailwindClassnames.basicSubHeading}>
        {t("Matching images")}
      </h4>
      <p className={`${tailwindClassnames.helperText} mt-1`}>
        {t(
          "{{matched}} of {{photos}} images in the export were matched to uploaded files.",
          {
            matched: number(matching.matched),
            photos: number(matching.photos),
          },
        )}
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {MATCH_STEPS.filter(
          ({ key }) => (matching.by_step?.[key] ?? 0) > 0,
        ).map(({ key, label }) => (
          <li key={key} className={tailwindClassnames.helperText}>
            {t(label, { total: number(matching.by_step?.[key] ?? 0) })}
          </li>
        ))}
      </ul>

      {unmatchedReasons.length > 0 && (
        <>
          <p className={`${tailwindClassnames.helperText} mt-2`}>
            {t("Images not matched:")}
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {unmatchedReasons.map((reason) => (
              <li key={reason} className={tailwindClassnames.helperText}>
                {t("{{reason}}: {{total}}", {
                  reason: t(unmatchedLabel(reason)),
                  total: number(matching.unmatched?.[reason] ?? 0),
                })}
              </li>
            ))}
          </ul>
        </>
      )}

      {matching.shifted_photos > 0 && (
        <div
          role="status"
          className="mt-2 rounded-lg border border-primary-orange/40 bg-primary-orange/10 p-2.5"
        >
          <p className={tailwindClassnames.warningHeader}>
            {t("Times in Wildlife Insights look edited")}
          </p>
          <p className={`${tailwindClassnames.helperText} mt-1`}>
            {t(
              "{{total}} images were matched by working out the difference between the two clocks. Check a few of them before you rely on this.",
              { total: number(matching.shifted_photos) },
            )}
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {shown.map((deployment) => (
              <li
                key={deployment.wi_deployment_id}
                className={tailwindClassnames.helperText}
              >
                {t(
                  "{{deployment}} — time difference {{shift}}, {{total}} images matched this way",
                  {
                    deployment: deployment.wi_deployment_id,
                    shift: formatClockShift(deployment.shift_seconds ?? 0),
                    total: number(deployment.shifted_photos),
                  },
                )}
              </li>
            ))}
          </ul>
          {serverDroppedSome ? (
            <p className={`${tailwindClassnames.helperText} mt-1`}>
              {t(
                "…and other deployments with a time difference, {{total}} more images matched this way",
                { total: number(imagesNotShown) },
              )}
            </p>
          ) : (
            hidden > 0 && (
              <p className={`${tailwindClassnames.helperText} mt-1`}>
                {t("…and {{total}} more deployments with a time difference", {
                  total: number(hidden),
                })}
              </p>
            )
          )}
        </div>
      )}

      {matching.akili_photos_awaiting_time > 0 && (
        <p className={`${tailwindClassnames.helperText} mt-2`}>
          {t(
            "{{total}} uploaded images are still having their time read. Run this bundle again in a few minutes to match the rest.",
            { total: number(matching.akili_photos_awaiting_time) },
          )}
        </p>
      )}
    </div>
  );
};

/**
 * The identifications summary line, with the AI suggestions appended when there are any
 * (AKL-754).
 *
 * `create` counts a person's identifications only; a Wildlife Insights AI guess stored as a
 * suggestion is neither created nor skipped. Without this suffix a bundle whose guesses were
 * all stored reads "0 of 1,000 identifications", which looks like a failure. With no
 * suggestions the line keeps its pre-AKL-754 wording exactly, so older reports read as before.
 * @param counts the section's or the totals' identification counts
 * @param unit `identifications` for a project's own line, `rows` for the multi-project headline
 * @param t the translation function
 * @param number the reader's number formatter
 * @returns the sentence to display
 */
export const identificationsSummary = (
  counts: MigrationIdentificationCounts,
  unit: "identifications" | "rows",
  t: TFunction,
  number: (value: number) => string,
): string => {
  const suggestions = counts.predictions_created ?? 0;
  const params = {
    create: number(counts.create),
    rows: number(counts.rows),
    suggestions: number(suggestions),
  };
  if (unit === "identifications") {
    return suggestions > 0
      ? t(
          "{{create}} of {{rows}} identifications, plus {{suggestions}} AI suggestions",
          params,
        )
      : t("{{create}} of {{rows}} identifications", params);
  }
  return suggestions > 0
    ? t(
        "{{create}} of {{rows}} rows, plus {{suggestions}} AI suggestions",
        params,
      )
    : t("{{create}} of {{rows}} rows", params);
};

/**
 * The sentence(s) for the Wildlife Insights AI guesses a migration stores as suggestions
 * (AKL-754), or null when there are none to mention.
 *
 * Two independent sentences rather than one with optional clauses, so each language can
 * phrase them its own way: what this run stores (future tense on a preview, past tense on a
 * result) and what an earlier run had already stored. Both counts are optional in the type
 * because a report stored before AKL-754 has neither; missing reads as 0 and renders nothing.
 * @param counts the section's identification counts
 * @param applied true when rendering the outcome of an apply rather than a preview
 * @param t the translation function
 * @param number the reader's number formatter
 * @returns the paragraph text, or null
 */
export const suggestionsText = (
  counts: MigrationIdentificationCounts,
  applied: boolean,
  t: TFunction,
  number: (value: number) => string,
): string | null => {
  const created = counts.predictions_created ?? 0;
  const alreadyImported = counts.predictions_already_imported ?? 0;
  const sentences: string[] = [];
  if (created > 0) {
    sentences.push(
      applied
        ? t(
            "{{total}} Wildlife Insights AI guesses were brought across as suggestions, for images nobody had identified. Accept, reject or change them in Photo Review; akili's own AI will still look at these images.",
            { total: number(created) },
          )
        : t(
            "{{total}} Wildlife Insights AI guesses will be brought across as suggestions, for images nobody had identified. Accept, reject or change them in Photo Review; akili's own AI will still look at these images.",
            { total: number(created) },
          ),
    );
  }
  if (alreadyImported > 0) {
    sentences.push(
      t(
        "{{total}} Wildlife Insights AI guesses were already brought across by an earlier migration.",
        { total: number(alreadyImported) },
      ),
    );
  }
  return sentences.length > 0 ? sentences.join(" ") : null;
};

interface CountRowProps {
  label: string;
  create: number;
  matched: number;
  note?: string;
}

export const CountRow: React.FC<CountRowProps> = ({
  label,
  create,
  matched,
  note,
}) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className={tailwindClassnames.basicLabel}>{t(label)}</span>
      <span className={tailwindClassnames.helperText}>
        {t("{{create}} new, {{matched}} already in akili", {
          create: formatNumber(create, i18n.language),
          matched: formatNumber(matched, i18n.language),
        })}
        {note ? ` — ${note}` : ""}
      </span>
    </div>
  );
};

interface MigrationReportProps {
  report: MigrationPlan;
  /** True when this is the outcome of an apply rather than a preview of one. */
  applied?: boolean;
}

/**
 * The projects line of the totals headline. Four full sentences rather than a base plus
 * optional fragments, so each language can order the clauses its own way. "To be created"
 * only appears on a plan (AKL-816); "not migrated" covers blocked projects only.
 *
 * A report from an API older than AKL-816 has no `projects_to_create` and counts the
 * to-be-created projects under `projects_skipped`. The count is then derived from the
 * sections (a plan section with action "create" and no project id) and taken out of
 * the skipped figure, so the headline reads the same either way.
 */
const projectsHeadline = (
  totals: MigrationTotals,
  sections: MigrationProjectSection[],
  t: TFunction,
  number: (value: number) => string,
): string => {
  const derivedToCreate = sections.filter(
    (section) => section.action === "create" && section.project_id === null,
  ).length;
  const toCreateCount = totals.projects_to_create ?? derivedToCreate;
  const skippedCount =
    totals.projects_to_create === undefined
      ? Math.max(0, totals.projects_skipped - derivedToCreate)
      : totals.projects_skipped;
  const params = {
    migrated: number(totals.projects_migrated),
    total: number(totals.projects),
    created: number(toCreateCount),
    skipped: number(skippedCount),
  };
  const toBeCreated = toCreateCount > 0;
  const skipped = skippedCount > 0;
  if (toBeCreated && skipped) {
    return t(
      "{{migrated}} of {{total}}, {{created}} to be created, {{skipped}} not migrated",
      params,
    );
  }
  if (toBeCreated) {
    return t("{{migrated}} of {{total}}, {{created}} to be created", params);
  }
  if (skipped) {
    return t("{{migrated}} of {{total}}, {{skipped}} not migrated", params);
  }
  return t("{{migrated}} of {{total}}", params);
};

interface ProjectSectionProps {
  section: MigrationProjectSection;
  samples: Record<string, string[]>;
  applied: boolean;
  /** Collapsed by default when the bundle holds several projects. */
  startOpen: boolean;
}

/**
 * One Wildlife Insights project's outcome.
 *
 * A `<details>` rather than a flat block: a bundle can hold a dozen projects, and a reader
 * scanning for the one that went wrong needs the headline first. Open by default when
 * there is only one, because then there is nothing to scan.
 */
const ProjectSection: React.FC<ProjectSectionProps> = ({
  section,
  samples,
  applied,
  startOpen,
}) => {
  const { t, i18n } = useTranslation();
  const number = (value: number) => formatNumber(value, i18n.language);

  const identifications = section.identifications;
  const skipped = identifications.skipped ?? {};
  const skippedTotal = Object.values(skipped).reduce(
    (total, value) => total + (value ?? 0),
    0,
  );
  const wasSkipped = section.action === "skipped";
  const templateSentence = deploymentTemplateText(
    section.deployment_template,
    applied,
    t,
  );
  // On a plan, a project the migration will create has no row yet, so its
  // identifications cannot be counted; say what will happen instead of "0 of N".
  const toBeCreated =
    !applied && section.action === "create" && section.project_id === null;
  const suggestionsSentence = suggestionsText(
    identifications,
    applied,
    t,
    number,
  );

  return (
    <details
      open={startOpen}
      className={`rounded-lg border p-3 ${
        wasSkipped
          ? "border-primary-orange/40 bg-primary-orange/5"
          : "border-off-white-dark/70 dark:border-storm-blue-border"
      }`}
    >
      <summary className="cursor-pointer">
        <span className={tailwindClassnames.basicLabel}>
          {section.project_name ||
            section.wi_project_id ||
            t("Unnamed project")}
        </span>
        <span className={`${tailwindClassnames.helperText} ml-2`}>
          {wasSkipped
            ? t("not migrated")
            : toBeCreated
              ? t("will be created")
              : identificationsSummary(
                  identifications,
                  "identifications",
                  t,
                  number,
                )}
        </span>
      </summary>

      <div className="mt-2 space-y-3">
        {section.blocking.length > 0 && (
          <div role="alert">
            <ul className="list-disc space-y-1 pl-5">
              {section.blocking.map((notice, index) => (
                <li
                  key={`${notice.code}-${index}`}
                  className={tailwindClassnames.helperText}
                >
                  {migrationNoticeText(notice, t)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!wasSkipped && (
          <div className="divide-y divide-off-white-dark/60 dark:divide-storm-blue-border">
            <CountRow
              label="Deployments"
              create={section.deployments.create}
              matched={section.deployments.matched}
            />
            <CountRow
              label="Locations"
              create={section.locations.create}
              matched={section.locations.matched}
              note={
                section.locations.skipped_no_coordinates
                  ? t("{{total}} without coordinates", {
                      total: number(section.locations.skipped_no_coordinates),
                    })
                  : undefined
              }
            />
            <CountRow
              label="Cameras"
              create={section.sensors.create}
              matched={section.sensors.matched}
            />
          </div>
        )}

        {/* AKL-817. The migrated deployment fields only show if the project uses the
            Wildlife Insights template, so the user is told what happens to it before they
            confirm. `blocked` is already named in the bundle-level alert and `unavailable`
            in "Worth checking"; repeating either here would be noise. */}
        {!wasSkipped && templateSentence && (
          <p className={tailwindClassnames.helperText}>{templateSentence}</p>
        )}

        {identifications.media_marked_reviewed > 0 && (
          <p className={tailwindClassnames.helperText}>
            {t(
              "{{total}} images will be marked as reviewed, because a person identified them in Wildlife Insights.",
              { total: number(identifications.media_marked_reviewed) },
            )}
          </p>
        )}

        {/* AKL-754. Wildlife Insights AI guesses stored as suggestions are neither created
            identifications nor skipped rows, so they get their own sentence. */}
        {suggestionsSentence && (
          <p className={tailwindClassnames.helperText}>{suggestionsSentence}</p>
        )}

        {/* AKL-1125. How the project's images were matched to uploaded files. Absent on a
            report stored before it existed, and pointless when nothing was matched against
            (a project still to be created has no images to look at). */}
        {!wasSkipped && section.matching && section.matching.photos > 0 && (
          <MatchingBlock matching={section.matching} />
        )}

        {/* Every skipped row accounted for by reason. A migration that silently drops a
            third of someone's data while reporting success is the failure this prevents. */}
        {skippedTotal > 0 && (
          <div>
            <h4 className={tailwindClassnames.basicSubHeading}>
              {t("Not migrated ({{total}} rows)", {
                total: number(skippedTotal),
              })}
            </h4>
            <dl className="mt-1.5 space-y-2">
              {SKIP_EXPLANATIONS.filter(
                ({ key }) => (skipped[key] ?? 0) > 0,
              ).map(({ key, label, detail }) => (
                <div key={key}>
                  <dt className={tailwindClassnames.basicLabel}>
                    {t(label)} — {number(skipped[key] ?? 0)}
                  </dt>
                  {detail && (
                    <dd className={tailwindClassnames.helperText}>
                      {t(detail)}
                    </dd>
                  )}
                  {samples?.[key]?.length > 0 && (
                    <dd
                      className={`${tailwindClassnames.helperText} mt-0.5 italic`}
                    >
                      {t("For example")}: {samples[key].slice(0, 5).join(", ")}
                    </dd>
                  )}
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </details>
  );
};

const MigrationReport: React.FC<MigrationReportProps> = ({
  report,
  applied = false,
}) => {
  const { t, i18n } = useTranslation();
  const number = (value: number) => formatNumber(value, i18n.language);

  const totals = report.totals;
  const sections = report.projects ?? [];
  const many = sections.length > 1;

  return (
    <div className="space-y-4">
      {/* Bundle-level blockers first and unmissable: these stop everything, and the apply
          button is disabled while any remain. A single project's problems are shown on its
          own section instead, because they only skip that project. */}
      {report.blocking.length > 0 && (
        <div
          className="rounded-lg border border-primary-orange/40 bg-primary-orange/10 p-3"
          role="alert"
        >
          <p className={tailwindClassnames.warningHeader}>
            {applied
              ? t("This migration could not be completed")
              : t("This migration cannot run yet")}
          </p>
          <ul className="mt-1.5 list-disc space-y-1 pl-5">
            {report.blocking.map((notice, index) => (
              <li
                key={`${notice.code}-${index}`}
                className={tailwindClassnames.helperText}
              >
                {migrationNoticeText(notice, t)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {sections.length > 0 && (
        <section>
          <h3 className={tailwindClassnames.basicSubHeading}>
            {applied ? t("What was migrated") : t("What will be migrated")}
          </h3>

          {/* The headline. Shown only for a multi-project bundle — with one project the
              per-project section already IS the headline, and repeating it reads as
              padding. */}
          {many && totals && (
            <div className="mt-1.5 divide-y divide-off-white-dark/60 dark:divide-storm-blue-border">
              <div className="flex items-baseline justify-between gap-3 py-1">
                <span className={tailwindClassnames.basicLabel}>
                  {t("Projects")}
                </span>
                <span className={tailwindClassnames.helperText}>
                  {projectsHeadline(totals, sections, t, number)}
                </span>
              </div>
              <CountRow
                label="Deployments"
                create={totals.deployments.create}
                matched={totals.deployments.matched}
              />
              <CountRow
                label="Cameras"
                create={totals.sensors.create}
                matched={totals.sensors.matched}
              />
              <div className="flex items-baseline justify-between gap-3 py-1">
                <span className={tailwindClassnames.basicLabel}>
                  {t("Identifications")}
                </span>
                <span className={tailwindClassnames.helperText}>
                  {identificationsSummary(
                    totals.identifications,
                    "rows",
                    t,
                    number,
                  )}
                </span>
              </div>
            </div>
          )}

          <div className="mt-2 space-y-2">
            {sections.map((section, index) => (
              <ProjectSection
                key={section.wi_project_id ?? `project-${index}`}
                section={section}
                samples={report.samples ?? {}}
                applied={applied}
                startOpen={!many || section.action === "skipped"}
              />
            ))}
          </div>
        </section>
      )}

      {report.warnings.length > 0 && (
        <section>
          <h3 className={tailwindClassnames.basicSubHeading}>
            {t("Worth checking")}
          </h3>
          <ul className="mt-1.5 list-disc space-y-1 pl-5">
            {report.warnings.map((notice, index) => (
              <li
                key={`${notice.code}-${index}`}
                className={tailwindClassnames.helperText}
              >
                {migrationNoticeText(notice, t)}
              </li>
            ))}
          </ul>
        </section>
      )}

      <details className="rounded-lg border border-off-white-dark/70 p-2.5 dark:border-storm-blue-border">
        <summary className={`${tailwindClassnames.basicLabel} cursor-pointer`}>
          {t("Bundle contents")}
        </summary>
        <ul className="mt-1.5 space-y-0.5">
          {report.bundle.files.map((file) => (
            <li key={file.name} className={tailwindClassnames.helperText}>
              {file.name} — {t("{{count}} columns", { count: file.columns })}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
};

export default MigrationReport;
